import React from 'react';
import {
  ProductOptionsProvider,
  Link,
  Image,
  ProductPrice,
  OptionWithValues,
  useProductOptions,
  BuyNowButton,
  AddToCartButton,
} from '@shopify/hydrogen';
import clsx from 'clsx';
import * as R from 'ramda';

//components
import Container from '../../components/global/ContainerClient.client';
import Text from '../../components/custom/Text.client';
// import ProductOptionsColour from './Product.client'
import ProductAdditionalInfo from './ProductAdditionalInfo.client';
import ProductCarousel from './ProductCarousel.client';
import ProductDetailsOptionsColour from './ProductDetailsOptionsColour.client';
import ProductDetailsOptionSize from './ProductDetailsOptionSize.client';
import ProductDetailsOptionsGeneric from './ProductDetailsOptionsGeneric.client';
import FrequentlyBoughtTogether from './FrequentlyBoughtTogether.client';
import ProductPrices from './ProductPrices.client';
import SanityRichText from './SanityRichTextClient.client';
import SanityImage from '../global/SanityImage.client';
import ProductDetailsSizeGuide from './ProductDetailsSizeGuide.client';

//types
import type {
  ProductWithNodes,
  SanityProductPage,
  SanityProductSettings,
  SantiyCollectionLevel,
  Brand,
} from '../../types';
import type {ProductVariant} from '@shopify/hydrogen/storefront-api-types';
import type {
  Maybe,
  Image as ImageProps,
} from '@shopify/hydrogen/storefront-api-types';

//utils
import {hasMultipleProductOptions} from '../../utils/productOptions';

//assets
import SizeGuide from '../../../public/size-guide.png';
import PaymentMethods from '../../../public/payment-methods.png';

//config
import sanityConfig from '../../../sanity.config';

interface ColourOptionsWithThumbnail {
  colour: string;
  thumbnail: Maybe<ImageProps> | undefined;
  available?: boolean;
  sizes?: {size: string | undefined; available: boolean}[];
}

interface Props {
  initialVariantId?: ProductVariant['id'];
  sanityProduct: SanityProductPage;
  storefrontProduct: ProductWithNodes;
  frequentlyBoughtTogetherProducts: ProductWithNodes[] | null;
  sanityProductSettings: SanityProductSettings | null | undefined;
  sanityVendor:
    | Pick<Brand, 'shopify_vendor_node' | 'brand_size_guide'>
    | null
    | undefined;
}

export default function ProductDetailsWrapper({
  // initialVariantId,
  sanityProduct,
  storefrontProduct,
  frequentlyBoughtTogetherProducts,
  sanityProductSettings,
  sanityVendor,
}: Props) {
  const allVariants = storefrontProduct.variants.nodes;
  const firstAvailableVariant = allVariants.find((v) => v.availableForSale);
  const multipleProductOptions = hasMultipleProductOptions(
    storefrontProduct.options,
  );

  const allColourOptions =
    multipleProductOptions &&
    storefrontProduct?.options?.find(
      (o) => o.name === 'Colour' || o.name === 'Color',
    )?.values;

  const allColourOptionsWithThumbnail = allColourOptions
    ? allColourOptions.map((colour) => {
        const variantsOfThisColour = allVariants.filter(
          (v) =>
            v?.selectedOptions?.find(
              (o) => o.name === 'Colour' || o.name === 'Color',
            )?.value === colour,
        );
        const isColourAvailable = variantsOfThisColour.find(
          (v) => v.availableForSale,
        );
        const sizesAvailable = variantsOfThisColour.map((c) => ({
          available: c.availableForSale,
          size: c?.selectedOptions?.find((o) => o.name === 'Size')?.value,
        }));
        return {
          colour: colour,
          thumbnail: allVariants.find((v) => v.title.includes(colour))?.image,
          available: isColourAvailable ? true : false,
          sizes: sizesAvailable,
        };
      })
    : undefined;

  return (
    <ProductOptionsProvider
      data={storefrontProduct}
      initialVariantId={firstAvailableVariant?.id}
    >
      <ProductDetails
        sanityProduct={sanityProduct}
        storefrontProduct={storefrontProduct}
        allColourOptionsWithThumbnail={allColourOptionsWithThumbnail}
        firstAvailableVariant={firstAvailableVariant}
        frequentlyBoughtTogetherProducts={frequentlyBoughtTogetherProducts}
        sanityProductSettings={sanityProductSettings}
        sanityVendor={sanityVendor}
        allVariants={allVariants}
      />
    </ProductOptionsProvider>
  );
}

function ProductDetails({
  // initialVariantId,
  // sanityProduct,
  storefrontProduct,
  firstAvailableVariant,
  allColourOptionsWithThumbnail,
  frequentlyBoughtTogetherProducts,
  sanityProductSettings,
  sanityVendor,
  allVariants,
}: Props & {
  allColourOptionsWithThumbnail?: ColourOptionsWithThumbnail[] | undefined;
  firstAvailableVariant: ProductVariant | undefined;
  allVariants: ProductVariant[];
}) {
  const {options, selectedVariant} = useProductOptions();
  const multipleProductOptions = hasMultipleProductOptions(
    storefrontProduct.options,
  );
  const media = storefrontProduct?.media?.nodes;
  const [quantity, setQuantity] = React.useState(1);
  const quantityAvailable = !R.isNil(selectedVariant)
    ? (selectedVariant.quantityAvailable as number)
    : 0;

  const [showDescription, setShowDescription] = React.useState(false);

  const collectionStructure =
    sanityProductSettings?.collection_structure?.collections;
  const allCollections = storefrontProduct.collections?.nodes;

  const findNestedLevel = (
    data: SantiyCollectionLevel[],
    gid: string,
    level: number = 1,
  ): number => {
    if (!data || !Array.isArray(data)) {
      return -1;
    }

    return data.reduce((acc: number, item: SantiyCollectionLevel): number => {
      if (item.this_collection && item.this_collection.store.gid === gid) {
        return level;
      }

      if (item.associated_collections) {
        const nestedLevel = findNestedLevel(
          item.associated_collections.flatMap((ac) => ac.collections),
          gid,
          level + 1,
        );
        return nestedLevel !== -1 ? nestedLevel : acc;
      }

      return acc;
    }, -1);
  };

  const gidExists = (
    data: SantiyCollectionLevel[],
    gidToFind: string,
  ): boolean => {
    if (!data || !Array.isArray(data)) {
      return false;
    }

    return data.some((item) =>
      item.this_collection.store.gid === gidToFind
        ? true
        : item.associated_collections
        ? item.associated_collections.some((ac) =>
            gidExists(ac.collections, gidToFind),
          )
        : false,
    );
  };

  const collectionsInHierarchy =
    collectionStructure &&
    allCollections?.filter((c) => gidExists(collectionStructure, c.id));
  const collectionHierarachyData = collectionsInHierarchy?.map((c) => {
    return {
      title: c.title,
      handle: c.handle,
      level: collectionStructure
        ? findNestedLevel(collectionStructure, c.id)
        : -1,
    };
  });
  const level1Colleciton = collectionHierarachyData?.find((c) => c.level === 1);
  const level2Colleciton = collectionHierarachyData?.find((c) => c.level === 2);
  const level3Colleciton = collectionHierarachyData?.find((c) => c.level === 3);

  return (
    <div className="">
      <Container>
        <div className="pt-6">
          <nav className="mb-[50px] flex flex-wrap items-center">
            <Link
              to="/"
              className="font-normal transition-colors duration-200 ease-out hover:text-hk-orange"
            >
              Home
            </Link>
            {level1Colleciton && (
              <>
                <span className="mx-2 inline-block font-normal">/</span>
                <Link
                  to={`/collections/${level1Colleciton?.handle}`}
                  className="font-normal transition-colors duration-200 ease-out hover:text-hk-orange"
                >
                  {level1Colleciton.title}
                </Link>
              </>
            )}
            {level2Colleciton && (
              <>
                <span className="mx-2 inline-block font-normal">/</span>
                <Link
                  to={`/collections/${level2Colleciton?.handle}`}
                  className="font-normal transition-colors duration-200 ease-out hover:text-hk-orange"
                >
                  {level2Colleciton.title}
                </Link>
              </>
            )}
            {level3Colleciton && (
              <>
                <span className="mx-2 inline-block font-normal">/</span>
                <Link
                  to={`/collections/${level3Colleciton?.handle}`}
                  className="font-normal transition-colors duration-200 ease-out hover:text-hk-orange"
                >
                  {level3Colleciton.title}
                </Link>
              </>
            )}
          </nav>
          <div className="product-main mb-8 grid gap-4 lg:mb-[100px] lg:grid-cols-12">
            <ProductCarousel
              media={media}
              allColourOptionsWithThumbnail={allColourOptionsWithThumbnail}
            />
            <div className="text-md lg:col-span-5 lg:text-[20px]">
              <h2 className="mb-1 text-[24px] font-medium">
                {storefrontProduct.title}
              </h2>
              <div className="mb-[18px]">
                {selectedVariant?.sku && (
                  <span className="text-md font-normal">
                    產品編號: {selectedVariant?.sku}
                  </span>
                )}
              </div>
              {/* <div className="relative mb-12 hidden text-[20px] font-normal leading-relaxed lg:block">
                <div
                  className="line-clamp-4"
                  style={{WebkitLineClamp: showDescription ? 'none' : 4}}
                >
                  {storefrontProduct.description}
                </div>
                {storefrontProduct?.description &&
                  storefrontProduct?.description?.length > 240 && (
                    <button
                      className={clsx([
                        'absolute top-full left-0',
                        showDescription && 'hidden',
                      ])}
                      onClick={() => setShowDescription(() => true)}
                    >
                      <Text tag="p" type="paragraph" className="text-hk-orange">
                        {sanityProductSettings?.product_settings_main
                          ?.read_more_label ?? 'read more'}
                      </Text>
                    </button>
                  )}
                {storefrontProduct.descriptionHtml && <div className="" dangerouslySetInnerHTML={{ __html: storefrontProduct.descriptionHtml }} />}
              </div> */}

              <div className="mt-[20px] flex flex-col justify-between space-y-3 lg:flex-row lg:space-x-4 lg:space-y-0">
                {/* product vendor/brand */}
                <div className="brand flex space-x-1">
                  <span className="whitespace-nowrap font-medium">
                    {sanityProductSettings?.product_settings_translations
                      ?.brand_label ?? 'Brand'}{' '}
                    :
                  </span>
                  <Link
                    to={`/collections/vendor?q=${storefrontProduct.vendor}`}
                  >
                    <span className="font-normal">
                      {storefrontProduct.vendor}
                    </span>
                  </Link>
                </div>
                {/* product type */}
                <div className="product-type flex space-x-1">
                  <span className="whitespace-nowrap font-medium">
                    {sanityProductSettings?.product_settings_translations
                      ?.product_type_label ?? 'Product Type'}{' '}
                    :
                  </span>
                  <span className="font-normal">
                    {storefrontProduct.productType}
                  </span>
                </div>
              </div>
              {/* price */}
              <div className="price mt-3 flex space-x-1 lg:mt-[20px] ">
                <span className="font-medium">
                  {sanityProductSettings?.product_settings_translations
                    ?.price_label ?? 'Price'}{' '}
                  :
                </span>
                <ProductPrices
                  storefrontProduct={storefrontProduct}
                  type="details"
                />
              </div>
              <div className="product-options--colour mt-3 flex flex-col lg:mt-[20px] lg:flex-row lg:items-center">
                {multipleProductOptions &&
                  multipleProductOptions &&
                  (options as OptionWithValues[])?.map(
                    ({name, values}, index) => {
                      // only render the colour variant options
                      switch (name) {
                        case 'Colour':
                        case 'Color':
                          return (
                            <div className="order-2 flex w-full flex-1 space-x-4 lg:order-1 lg:mr-4">
                              <span className="mt-9 font-medium">
                                {sanityProductSettings
                                  ?.product_settings_translations
                                  ?.colour_label ?? 'Colour'}{' '}
                                :
                              </span>
                              <div className="flex-1">
                                <ProductDetailsOptionsColour
                                  key={`${name}-${index}-${storefrontProduct.handle}`}
                                  name={name}
                                  values={values}
                                  allColourOptionsWithThumbnail={
                                    allColourOptionsWithThumbnail
                                  }
                                />
                              </div>
                            </div>
                          );
                        case 'Model':
                          return (
                            <div className="order-2 flex w-full flex-1 items-center space-x-4 lg:order-1 lg:mr-4">
                              <span className="font-medium">{name}: </span>
                              <div className="flex-1">
                                <ProductDetailsOptionsGeneric
                                  key={`${name}-${index}-${storefrontProduct.handle}`}
                                  name={name}
                                  values={values}
                                  availableVariants={storefrontProduct?.variants?.nodes?.filter(
                                    (v) => v.availableForSale,
                                  )}
                                />
                              </div>
                            </div>
                          );
                        default:
                          return null;
                      }
                    },
                  )}
                <div className="order-1 w-[114px] self-end lg:order-2 lg:self-center">
                  <ProductDetailsSizeGuide
                    size_guide={
                      sanityProductSettings?.product_settings_main
                        ?.default_size_guide
                    }
                    size_guide_label={
                      sanityProductSettings?.product_settings_main
                        ?.size_guide_label
                    }
                    size_guide_icon={
                      sanityProductSettings?.product_settings_main
                        ?.size_guide_icon
                    }
                    sanityVendor={sanityVendor}
                  />
                </div>
              </div>
              <div className="mb-[28px] flex flex-col space-y-3 lg:mt-3 lg:flex-row lg:space-y-0 lg:space-x-8">
                <div className="quantity order-2 flex-1 lg:order-1">
                  <span className="mb-3 block font-medium">
                    {sanityProductSettings?.product_settings_translations
                      ?.quantity_label ?? 'Quantity'}{' '}
                    :
                  </span>
                  <div className="">
                    <div className="item-center relative flex min-h-[38px] min-w-[180px] overflow-hidden rounded-md bg-hk-deep-gray text-center">
                      <div className="absolute left-0 top-0 h-full">
                        <button
                          onClick={() =>
                            setQuantity((prevValue) =>
                              prevValue !== 1 ? prevValue - 1 : 1,
                            )
                          }
                          className="h-full bg-black px-4 text-[24px] text-white"
                        >
                          -
                        </button>
                      </div>
                      <div className="flex w-full items-center">
                        {/* <span className="w-full">{quantity}</span> */}
                        <input
                          className="w-full bg-transparent px-[50px] text-center outline-none"
                          value={quantity}
                          type="text"
                          onChange={(e) => {
                            if (e.target.value) {
                              const newQuantity = parseInt(
                                e.target.value.replace(/[^0-9]+/g, ''),
                              );
                              if (newQuantity < quantityAvailable) {
                                setQuantity(newQuantity);
                              }
                              if (newQuantity >= quantityAvailable) {
                                setQuantity(quantityAvailable);
                              }
                            }
                          }}
                        />
                      </div>
                      <div className="absolute right-0 top-0 h-full">
                        <button
                          onClick={() =>
                            setQuantity((prevValue) =>
                              prevValue < quantityAvailable
                                ? prevValue + 1
                                : prevValue,
                            )
                          }
                          className="h-full bg-black px-4 text-[24px] text-white"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                {multipleProductOptions &&
                  (options as OptionWithValues[])?.map(
                    ({name, values}, index) => {
                      // only render the colour variant options
                      switch (name) {
                        case 'Size':
                          return (
                            <div className="product-options--size order-1 lg:order-2">
                              <span className="mb-3 block font-medium">
                                {sanityProductSettings
                                  ?.product_settings_translations?.size_label ??
                                  'Size'}{' '}
                                :
                              </span>
                              <ProductDetailsOptionSize
                                key={`${name}-${index}-${storefrontProduct.handle}`}
                                name={name}
                                values={values}
                                allColourOptionsWithThumbnail={
                                  allColourOptionsWithThumbnail
                                }
                                allVariants={allVariants}
                              />
                            </div>
                          );
                        default:
                          return null;
                      }
                    },
                  )}
              </div>
              <div className="mb-[25px] grid gap-2 lg:grid-cols-2 lg:gap-[30px]">
                <AddToCartButton
                  className={clsx([
                    'w-full  py-3 text-[20px] font-normal uppercase transition-all duration-200 ease-out',
                    !selectedVariant?.availableForSale
                      ? 'bg-hk-deep-gray'
                      : 'bg-black text-hk-orange hover:bg-hk-orange hover:text-black ',
                  ])}
                  disabled={!selectedVariant?.availableForSale}
                  quantity={quantity}
                  variantId={selectedVariant?.id}
                >
                  {(!selectedVariant?.availableForSale &&
                    sanityProductSettings?.product_settings_translations
                      ?.sold_out_label) ??
                    'Out of Stock'}
                  {(selectedVariant?.availableForSale &&
                    sanityProductSettings?.product_settings_translations
                      ?.add_to_cart_label) ??
                    'Add to cart'}
                </AddToCartButton>
                {selectedVariant?.id && selectedVariant?.availableForSale && (
                  <BuyNowButton
                    className={clsx([
                      'w-full py-3 text-[20px] font-normal uppercase transition-all duration-200 ease-out',
                      !selectedVariant?.availableForSale
                        ? 'bg-hk-deep-gray'
                        : 'bg-hk-orange text-black hover:bg-black hover:text-hk-orange ',
                    ])}
                    disabled={!selectedVariant?.availableForSale}
                    quantity={quantity}
                    variantId={selectedVariant?.id}
                  >
                    {sanityProductSettings?.product_settings_translations
                      ?.buy_now_label ?? 'Buy now'}
                  </BuyNowButton>
                )}
                <div className="col-span-2">
                  <div
                    id="preproduct-pledge"
                    data-id={storefrontProduct.id?.replace(
                      'gid://shopify/Product/',
                      '',
                    )}
                    data-headless="true"
                  ></div>
                  <script
                    type="text/javascript"
                    async
                    src="https://preproduct.onrender.com/preproduct-embed.js"
                  ></script>
                </div>
              </div>
              <div className="special-offer mb-[25px] text-[18px] font-normal">
                {sanityProductSettings?.product_settings_translations
                  ?.shipping_messages_label ? (
                  <SanityRichText
                    richtext={
                      sanityProductSettings?.product_settings_translations
                        ?.shipping_messages_label
                    }
                  />
                ) : (
                  <>
                    ORDER{' '}
                    <span className="font-normal text-hk-orange">HKS$500</span>{' '}
                    FREE SHIPPING TO HONG KONG AND MACAU
                  </>
                )}
              </div>
              <div className="special-offer mb-[25px] text-[18px] font-normal">
                {sanityProductSettings?.product_settings_translations
                  ?.delivery_period_messages_label ??
                  'Expected Delivery Period'}{' '}
                :{' '}
                <span className="font-normal text-hk-orange">
                  1-4 business weeks
                </span>
              </div>
              <div className="">
                {/* <Image
                  src={PaymentMethods}
                  alt="Payment Methods"
                  width={520}
                  height={32}
                /> */}
                <SanityImage
                  dataset={sanityConfig.dataset}
                  layout="responsive"
                  projectId={sanityConfig.projectId}
                  sizes={['50vw, 100vw']}
                  src={
                    sanityProductSettings?.product_settings_main
                      ?.payment_methods?.asset._ref
                  }
                  className="h-full object-cover"
                />
              </div>
            </div>
          </div>
          <div className="product-additional mb-[100px] grid gap-8 lg:grid-cols-12 lg:gap-0">
            <div className="order-2 lg:order-1 lg:col-span-3">
              <FrequentlyBoughtTogether
                frequentlyBoughtTogetherProducts={
                  frequentlyBoughtTogetherProducts
                }
                title={
                  sanityProductSettings?.product_settings_translations
                    ?.frequently_bought_together_label
                }
                button_label={
                  sanityProductSettings?.product_settings_translations
                    ?.add_selected_to_cart_label
                }
                total_price_label={
                  sanityProductSettings?.product_settings_translations
                    ?.total_price_label
                }
              />
            </div>
            <div className="order-1 lg:order-2 lg:col-span-7 lg:col-start-6">
              <ProductAdditionalInfo
                description={
                  sanityProductSettings?.product_settings_main?.description_body
                }
                storefrontDescription={
                  storefrontProduct?.descriptionHtml ??
                  storefrontProduct?.description
                }
                descriptionLabel={
                  sanityProductSettings?.product_settings_main
                    ?.description_title
                }
                shippingLabel={
                  sanityProductSettings?.product_settings_main?.shipping_title
                }
                shipping={
                  sanityProductSettings?.product_settings_main?.shipping_body
                }
                readMoreLabel={
                  sanityProductSettings?.product_settings_main?.read_more_label
                }
              />
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}
